import dayjs from "dayjs";
import { sum } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MaterialOrderFilterType, valueToText } from "../../../configs/filterTypes";
import {
    WarningCancelMaterialOrder,
    WarningDeclineMaterialOrder,
    WarningDeclineReasonMaterialOrder,
} from "../../../configs/materialOrder";
import { MaterialOrder } from "../../../data/entities";
import { MATERIAL_ORDER_STATUS, ROLE } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import DeclineModal from "../../../shared/components/GenericTextboxModal";
import LoadingButton from "../../../shared/components/LoadingButton";
import PageLoader from "../../../shared/components/LoadingPage";
import DeclineReasonModal from "../../../shared/components/ReasonTextModal";
import Status from "../../../shared/components/Status";
import { api } from "../../../shared/hooks/api";
import { TitleText } from "../../../shared/styled/TitleBar";
import Calendar from "../../../shared/svg/Calendar";
import UserCheckIcon from "../../../shared/svg/UserCheckIcon";
import Template from "../../../templates/don_mua_hang.docx";
import { generateDocument } from "../../../utils/exportUtils";
import {
    convertNumberToVietnameseMoneyText,
    formatNumberToCustomStyle,
    formatNumberToCustomStyleWithoutDecimal,
} from "../../../utils/format";
import { error, success } from "../../../utils/toast";
import { getStatusColorTheme } from "../../MaterialOrders/Content/MaterialOrderTable";
import { ButtonRow } from "../../ProductOrder/create/Styled";
import { SingleMaterialOrder } from "../single";
import { MaterialOrderWrapper, StyledButton } from "../Styled";
import { AddImportMaterialModal } from "./ImportMaterialModal";
import StatusProgress from "./StatusProgress";
import {
    HeaderLeft,
    HeaderRight,
    HeaderWrapper,
    IconWrap,
    MetaBar,
    MetaDividerV,
    MetaItem,
    MetaLabel,
    MetaRow,
    MetaValue,
    TitleRow,
    UrgentDot,
    UrgentText
} from "./Styled";
import MaterialOrderTabNavigation from "./TabBar";

const processMaterialOrder = (materialOrder: MaterialOrder) => {
    let materials = [];
    if (materialOrder) {
        materials = materialOrder.materials.map((item: any) => {
            if (item.material) {
                return {
                    ...item.material,
                    missing_quantity: item.quantity,
                    missingQuantity: item.quantity,
                    provider_name: item.provider_name,
                    provider_price: item.provider_price,
                };
            }
            return item;
        });
        materialOrder.created_at = dayjs(materialOrder.created_at).format("YYYY-MM-DD");
    }
    return { ...materialOrder, materials };
};

const prepareMaterialOrder = (materialOrder: any) => {
    const processedMaterials = materialOrder.materials.map((material: any) => ({
        material_id: material.id,
        quantity: material.missing_quantity,
    }));

    const materialOrderPrice = Math.round(
        sum(materialOrder.materials.map((item: any) => item.provider_price * item.missing_quantity)),
    );

    const processedData = {
        ...materialOrder,
        provider_id: materialOrder.provider_id,
        materials: processedMaterials,
        purchasing_staff: materialOrder.purchasing_staff?.id,
        first_storage_keeper: materialOrder.first_storage_keeper?.id,
        second_storage_keeper: materialOrder.second_storage_keeper?.id,
        manager: materialOrder.manager?.id,
        logistic_manager: materialOrder.logistic_manager?.id,
        total_price: materialOrderPrice,
    };

    return processedData;
};

export const MaterialOrderDetail = () => {
    const { materialOrderId } = useParams();
    const [{ data, isLoading: isOrderDataLoading, setLocalData }, fetchMaterialOrder] = api.get(
        `/materials/orders/${materialOrderId}/`,
    );
    const materialOrder: MaterialOrder = useMemo(() => processMaterialOrder(data), [data]);
    const [{ data: historyData, isLoading: isHistoryDataLoading }, fetchMaterialOrderHistory] = api.get(
        `/materials/orders/${materialOrderId}/history`,
    );

    const [{ isUpdating }, setNewData] = api.patch(`/materials/orders/${materialOrderId}/`);
    const [{ isUpdating: isCompletingImport }, completeImport] = api.put(`/materials/orders/${materialOrderId}/complete-import/`);

    const [isView, setIsView] = useState(true);
    const [isOpenDeclineModal, setIsOpenDeclineModal] = useState(false);
    const [showDeclineReasonModal, setShowDeclineReasonModal] = useState(false);
    const isLoading: boolean = isOrderDataLoading || isHistoryDataLoading;

    // Material import
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [
        {
            data: materialImports,
            isLoading: isFetchingMaterialImports,
            error: fetchMaterialImportError,
            setLocalUrl: setGetMaterialImport,
        },
        fetchMaterialImports,
    ] = api.get(`/materials/orders/${materialOrder?.id}/imports/`);

    const {
        user: { role, id: userId },
    } = useContext(AuthenticationContext);

    const canEdit = useMemo(() => {
        return materialOrder.status === MATERIAL_ORDER_STATUS.OPEN && (!materialImports || materialImports.length === 0);
    }, [materialOrder.status, materialImports]);

    const canSendToStorage = () => {
        const isRoleAllowed = [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.PURCHASING_STAFF].includes(role);

        return isRoleAllowed && materialOrder.status === MATERIAL_ORDER_STATUS.OPEN;
    };

    const areStorageKeepersAssigned = (materialOrder: MaterialOrder): boolean => {
        if (materialOrder.purchasing_staff === null) return false;

        const requiredStorageKeepers = materialOrder.required_storage_keepers || [];

        // Check if all required storage keepers are assigned
        return requiredStorageKeepers.every((keeper) => {
            if (keeper === "first_storage_keeper") {
                return materialOrder.first_storage_keeper !== null;
            } else if (keeper === "second_storage_keeper") {
                return materialOrder.second_storage_keeper !== null;
            }
            return true; // For unsupported roles, assume they are "assigned"
        });
    };

    const canImportToStorage = () => {
        const isRoleAllowed = [ROLE.MANAGER, ROLE.STORAGE_KEEPER, ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);
        const isRestocking = [MATERIAL_ORDER_STATUS.IMPORTING].includes(materialOrder.status);

        if (!isRoleAllowed || !isRestocking) return false;
        if (role === ROLE.MANAGER) return true;

        const isFirstStorageKeeper = materialOrder.first_storage_keeper?.id === userId;
        const isSecondStorageKeeper = materialOrder.second_storage_keeper?.id === userId;

        return isFirstStorageKeeper || isSecondStorageKeeper;
    };

    const canCompleteImport = () => {
        const isRoleAllowed = [ROLE.MANAGER, ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);
        const isImporting = materialOrder.status === MATERIAL_ORDER_STATUS.IMPORTING;

        if (!isRoleAllowed || !isImporting) return false;

        if (role === ROLE.MANAGER) return true;

        const isFirstStorageKeeper = materialOrder.first_storage_keeper?.id === userId;
        const isSecondStorageKeeper = materialOrder.second_storage_keeper?.id === userId;

        return isFirstStorageKeeper || isSecondStorageKeeper;
    };

    useEffect(() => {
        setGetMaterialImport(`/materials/orders/${materialOrder.id}/imports/`);
    }, [materialOrder.id, setGetMaterialImport]);

    const onClickSendingToStorageKeeper = async () => {
        if (isUpdating) return;
        try {
            const preparedMaterialOrder: any = prepareMaterialOrder(materialOrder);
            preparedMaterialOrder.status = MATERIAL_ORDER_STATUS.IMPORTING;
            await setNewData(preparedMaterialOrder);
            await fetchMaterialOrder();
            success("THÀNH CÔNG", "Gửi cho thủ kho thành công");
        } catch (err) {
            error("Gửi cho thủ kho thất bại", err);
        }
    };

    const onClickCompleteImport = async () => {
        if (isCompletingImport) return;
        try {
            const payload: any = {};

            const isFirstStorageKeeper = materialOrder.first_storage_keeper?.id === userId;
            const isSecondStorageKeeper = materialOrder.second_storage_keeper?.id === userId;

            if (role === ROLE.MANAGER) {
                payload.is_first_storage_imported = true;
                payload.is_second_storage_imported = true;
            } else if (isFirstStorageKeeper) {
                payload.is_first_storage_imported = true;
            } else if (isSecondStorageKeeper) {
                payload.is_second_storage_imported = true;
            }

            await completeImport(payload);
            await fetchMaterialOrder();
            success("THÀNH CÔNG", "Hoàn thành nhập kho thành công");
        } catch (err) {
            error("Hoàn thành nhập kho thất bại", err);
        }
    };

    const onClickUpdateMaterialOrder = async () => {
        if (isUpdating) return;
        try {
            const preparedData = prepareMaterialOrder(materialOrder);

            await setNewData(preparedData);
            await fetchMaterialOrder();
            setIsView(true);
            success("THÀNH CÔNG", "Cập nhật đơn hàng thành công");
        } catch (err) {
            error("Cập nhật đơn hàng thất bại", err);
        }
    };

    const onClickDownloadingMaterialOrderForm = async () => {
        // for each grouped_materials_by_provider, construct a formData object
        if (!materialOrder) {
            error("Không có thông tin đơn hàng để tải xuống");
            return;
        }
        const calculateItemTotalPrice = (item: any) => {
            const itemPrice = item.material.price;
            const discount = item.material.discount || 0;
            const markup = item.material.markup || 0;
            return item.quantity *
                Math.ceil(
                    ((itemPrice * (100 - discount)) / 100) * ((100 + markup) / 100),
                );
        };
        for (const [provider, materials] of Object.entries(materialOrder.grouped_materials_by_provider || {})) {
            if (!materials || materials.length === 0) continue;
            if (!provider || !Array.isArray(materials)) continue;
            const selectedProvider = materials[0].material.providers.find((p: any) => p.provider.name === provider);
            const orderTotal = sum(materials.map(calculateItemTotalPrice));
            const formData = {
                provider_name: provider || "",
                address: selectedProvider?.provider?.address || "",
                tax_ID: selectedProvider?.provider?.tax_id || "",
                phone_number: selectedProvider?.provider?.phone || "",
                email: selectedProvider?.provider?.email || "",
                order_note: materialOrder.note || "",
                order_date: materialOrder.created_at || "",
                order_name: materialOrder.name || "",
                delivery_date: materialOrder.due_date || "",
                materials: materials.map((material: any, index: number) => ({
                    index: index + 1,
                    material_name: material?.material?.name || "",
                    material_code: material?.material?.material_code || "",
                    measurement_unit: material?.material?.measurement_unit || "",
                    quantity: formatNumberToCustomStyle(material?.quantity) || "",
                    price: formatNumberToCustomStyle(material?.material?.price) || "",
                    material_total_price: formatNumberToCustomStyleWithoutDecimal(calculateItemTotalPrice(material) || 0),
                })),
                order_total_price: formatNumberToCustomStyleWithoutDecimal(orderTotal) || "",
                total_value_in_text: convertNumberToVietnameseMoneyText(orderTotal) || "",

            };

            generateDocument(formData, Template, `don_mua_hang_${materialOrder.name}_${provider}`);
        }
    };
    const onClickDecline = async (reason?: string) => {
        if (isUpdating) return;
        try {
            const preparedMaterialOrder: any = prepareMaterialOrder(materialOrder);
            if (materialOrder.status === MATERIAL_ORDER_STATUS.OPEN) {
                preparedMaterialOrder.status = MATERIAL_ORDER_STATUS.CANCELLED;
            }
            preparedMaterialOrder.note = reason;
            await setNewData(preparedMaterialOrder);
            await fetchMaterialOrder();
            setIsOpenDeclineModal(false);
            success("THÀNH CÔNG", "Đã từ chối đơn hàng thành công");
        } catch (err) {
            error("Từ chối đơn hàng thất bại", err);
        }
    };

    const isEditable = () => {
        return (
            materialOrder.status === MATERIAL_ORDER_STATUS.OPEN || materialOrder.status === MATERIAL_ORDER_STATUS.IMPORTING
        );
    };

    const onToggleDeclineReasonModal = () => {
        setShowDeclineReasonModal((prev) => !prev);
    };

    const materialOrderInfoTab = isLoading ? (
        <PageLoader />
    ) : (
        <MaterialOrderWrapper>
            <SingleMaterialOrder
                materialOrder={materialOrder}
                mergeMaterialOrder={(updatedData: any) =>
                    setLocalData({
                        ...data,
                        ...updatedData,
                    })
                }
                fetchMaterialOrder={fetchMaterialOrder}
                isView={isView}
            />
        </MaterialOrderWrapper>
    );

    if (isLoading) return <PageLoader />;

    return (
        <>
            {isImportModalOpen && (
                <AddImportMaterialModal
                    materialOrder={materialOrder}
                    mergeMaterialOrder={(updatedData: any) =>
                        setLocalData({
                            ...data,
                            ...updatedData,
                        })
                    }
                    fetchMaterialImports={fetchMaterialImports}
                    fetchMaterialOrder={fetchMaterialOrder}
                    open={isImportModalOpen}
                    handleClose={() => setIsImportModalOpen(false)}
                />
            )}

            {isOpenDeclineModal && (
                <DeclineModal
                    open={isOpenDeclineModal}
                    title={
                        materialOrder.status === MATERIAL_ORDER_STATUS.OPEN
                            ? WarningCancelMaterialOrder.title
                            : WarningDeclineMaterialOrder.title
                    }
                    contentText={
                        materialOrder.status === MATERIAL_ORDER_STATUS.OPEN
                            ? WarningCancelMaterialOrder.context
                            : WarningDeclineMaterialOrder.context
                    }
                    reasonText={
                        materialOrder.status === MATERIAL_ORDER_STATUS.OPEN
                            ? WarningCancelMaterialOrder.reason
                            : WarningDeclineMaterialOrder.reason
                    }
                    handleConfirm={onClickDecline}
                    handleClose={() => setIsOpenDeclineModal(false)}
                    buttonText="Từ chối duyệt đơn hàng"
                    isLoading={isUpdating}
                />
            )}

            <DeclineReasonModal
                open={showDeclineReasonModal}
                title={WarningDeclineReasonMaterialOrder.title}
                contentText={[materialOrder.note]}
                handleClose={onToggleDeclineReasonModal}
            />

            <MaterialOrderWrapper>
                <HeaderWrapper>
                    <HeaderLeft>
                        <TitleRow>
                            <TitleText>
                                {materialOrder.name ? materialOrder.name : materialOrder.id}
                            </TitleText>
                            <Status
                                colorTheme={getStatusColorTheme(materialOrder.status)}
                                text={valueToText(
                                    MaterialOrderFilterType,
                                    "status",
                                    materialOrder.status
                                )}
                                width="124px"
                            />
                        </TitleRow>

                        <MetaRow>
                            <MetaBar>
                                {materialOrder?.is_urgent && (
                                    <>
                                        <UrgentDot />
                                        <UrgentText>Khẩn cấp</UrgentText>
                                        <MetaDividerV />
                                    </>
                                )}
                                {materialOrder?.due_date && (
                                    <MetaItem>
                                        <IconWrap>
                                            <Calendar />
                                        </IconWrap>
                                        <MetaLabel>Hạn</MetaLabel>
                                        <MetaValue>
                                            {dayjs(materialOrder.due_date).format("DD/MM/YYYY")}
                                        </MetaValue>
                                    </MetaItem>
                                )}
                            </MetaBar>
                        </MetaRow>
                    </HeaderLeft>

                    {!isView && (
                        <HeaderRight>
                            <StyledButton variant="text" onClick={() => setIsView(true)} disabled={isUpdating}>
                                Huỷ bỏ
                            </StyledButton>
                            <LoadingButton
                                variant="contained"
                                onClick={onClickUpdateMaterialOrder}
                                isLoading={isUpdating}
                                text="Cập nhật đơn hàng"
                            />
                        </HeaderRight>
                    )}

                    {isView && (
                        <HeaderRight>
                            {materialOrder.status !== MATERIAL_ORDER_STATUS.IMPORTING && (
                                <>
                                    {isEditable() && (
                                        <StyledButton variant="text" onClick={() => setIsOpenDeclineModal(true)}>
                                            Huỷ đơn hàng
                                        </StyledButton>
                                    )}
                                    {/* {isEditable() && (
                                        <StyledButton variant="text" onClick={() => setIsView(false)}>
                                            Chỉnh sửa
                                        </StyledButton>
                                    )} */}
                                </>
                            )}

                            {canSendToStorage() && (
                                <LoadingButton
                                    tooltipText={"Cần chọn thủ kho Review"}
                                    text={"Gửi tới thủ kho "}
                                    disabled={!areStorageKeepersAssigned(materialOrder) || isLoading || isUpdating}
                                    isLoading={isUpdating}
                                    onClick={onClickSendingToStorageKeeper}
                                    icon={<UserCheckIcon />}
                                />
                            )}

                            {canImportToStorage() && (
                                <ButtonRow>
                                    <StyledButton variant="contained" onClick={() => setIsImportModalOpen(true)}>
                                        Tiến hành nhập kho
                                    </StyledButton>
                                </ButtonRow>
                            )}

                            {canCompleteImport() && (
                                <LoadingButton
                                    text="Hoàn thành nhập kho"
                                    onClick={onClickCompleteImport}
                                    isLoading={isCompletingImport}
                                    disabled={isCompletingImport || isLoading}
                                />
                            )}

                            {canEdit && (
                                <ButtonRow>
                                    <StyledButton variant="contained" onClick={() => setIsView(false)}>
                                        Chỉnh sửa
                                    </StyledButton>
                                </ButtonRow>
                            )}
                            <StyledButton variant="contained" onClick={() => onClickDownloadingMaterialOrderForm()}>
                                Tải đơn mua hàng
                            </StyledButton>
                        </HeaderRight>
                    )}
                </HeaderWrapper>

                <StatusProgress status={materialOrder.status} />
                
                {isLoading || isFetchingMaterialImports ? (
                    <PageLoader />
                ) : (
                    <MaterialOrderTabNavigation
                        materialOrderInfoTab={materialOrderInfoTab}
                        materialOrder={materialOrder}
                        mergeMaterialOrder={(updatedData: any) =>
                            setLocalData({
                                ...data,
                                ...updatedData,
                            })
                        }
                        materialImports={materialImports}
                    />
                )}
            </MaterialOrderWrapper>
        </>
    );
};
